/**
 * ZEN ERP — PDF & Paylaşım Yardımcıları
 * Teklif, imalat fişi, etiket PDF'leri; WhatsApp paylaşımı; kargo etiketi HTML.
 */
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import JsBarcode from "jsbarcode";
import { toast } from "sonner";

import api from "../api";
import logo from "../assets/zen-logo.png";
import { MONEY, ono, fmtDate, shareUrl } from "../appUtils";
import { CURRENCY_SYMBOL } from "../mock";
import { productEn } from "../i18n";
import { renderTailorSheet } from "./tailorSheetPdf";
import { kanatChoice } from "./tulFonSheetModel";

// ── Konfigürasyon Sabitleri ───────────────────────────────────────────────────
export const SINEKLIK_BOYALI_KABIN_UCRETI = 3000;

/**
 * Gizli iframe ile HTML veya PDF blob URL yazdırır — yeni sekme/pencere açılmaz.
 * @param {string} htmlOrUrl  HTML string veya blob: URL
 * @param {boolean} isUrl     true → blob URL (PDF), false → HTML string
 * @param {string|null} blobToRevoke  Temizlik için URL.revokeObjectURL
 */
export function printViaIframe(htmlOrUrl, isUrl = false, blobToRevoke = null) {
  // Önceki kalmış print-frame varsa temizle
  document.querySelectorAll("[data-zen-print-frame]").forEach((el) => el.remove());

  const iframe = document.createElement("iframe");
  iframe.setAttribute("data-zen-print-frame", "1");
  iframe.setAttribute("title", "Yazdırma");
  Object.assign(iframe.style, {
    position: "fixed",
    top: "-9999px",
    left: "-9999px",
    width: "1px",
    height: "1px",
    opacity: "0",
    border: "none",
    pointerEvents: "none",
  });
  document.body.appendChild(iframe);

  const cleanup = () => {
    setTimeout(() => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      if (blobToRevoke) URL.revokeObjectURL(blobToRevoke);
    }, 3000);
  };

  if (isUrl) {
    iframe.src = htmlOrUrl;
    iframe.onload = () => {
      try { iframe.contentWindow.print(); } catch (_) { /* autoPrint() içinde tetiklenecek */ }
      cleanup();
    };
    setTimeout(cleanup, 8000); // zaman aşımı güvenliği
  } else {
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (doc) { doc.open(); doc.write(htmlOrUrl); doc.close(); }
    setTimeout(() => {
      try { iframe.contentWindow?.focus(); iframe.contentWindow?.print(); } catch (e) {
        console.warn("iframe print:", e);
      }
      cleanup();
    }, 500);
  }
}
export const SINEKLIK_AHSAP_DESEN_PCT = 20;

const PLISE14_EXTRAS = [
  { key: "cekme_cubugu", label: "Çekme Çubuğu", type: "adet", price: 9 },
  { key: "menfez", label: "Menfez", type: "m2", price: 17 },
  { key: "cerçeveli_sistem", label: "Çerçeveli Sistem (+13 EUR/m²)", type: "m2", price: 13 },
  { key: "zigil_aparati", label: "Zigıl Aparatı", type: "m2", price: 5 },
];
export const GROUP_EXTRAS = {
  plise25: [
    { key: "renk_farki", label: "Renk Farkı (beyaz/krem harici)", type: "m2", price: 1 },
    { key: "ucgen", label: "Üçgen", type: "adet", price: 35 },
    { key: "cati", label: "Çatı", type: "adet", price: 29 },
    { key: "cekme_cubugu", label: "Çekme Çubuğu", type: "adet", price: 8 },
    { key: "l_ayak", label: "L Ayak", type: "adet", price: 1 },
    { key: "c110", label: "C110 Sistem (Cam Balkon Sistemi)", type: "adet", price: 6 },
  ],
  plise14: PLISE14_EXTRAS,
  natty_plise: PLISE14_EXTRAS,
  metal_jaluzi_16_25: [
    { key: "egimli", label: "Eğimli / Açılı", type: "pct", pct: 100 },
    { key: "karisik_renk", label: "Karışık Renk", type: "pct_renk", pct: 50 },
    { key: "ayri_renk", label: "Ayrı Renk", type: "pct", pct: 50 },
    { key: "zigil_takimi", label: "Zıgıl Takımı", type: "adet", price: 9 },
    { key: "buton_takimi", label: "Buton Takımı", type: "adet", price: 7.5 },
  ],
  ahsap_jaluzi_25: [
    { key: "delux_elcek", label: "Delüx Elçek", type: "adet_tl", price: 105, displayStr: "+105₺/ad" },
    { key: "cok_renk", label: "Karışık Renk", type: "pct", pct: 50 },
    { key: "egimli", label: "Eğimli / Açılı", type: "pct", pct: 100 },
  ],
  ahsap_jaluzi_50: [
    { key: "delux_elcek", label: "Delüx Elçek", type: "adet_tl", price: 105, displayStr: "+105₺/ad" },
    { key: "cok_renk", label: "Karışık Renk", type: "pct", pct: 50 },
    { key: "egimli", label: "Eğimli / Açılı", type: "pct", pct: 100 },
  ],
};

// ── Temel Yardımcılar ─────────────────────────────────────────────────────────

export async function loadLogoDataUrl() {
  try {
    const res = await fetch(logo);
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.readAsDataURL(blob);
    });
  } catch (e) { return null; }
}

export async function fetchRates() {
  try {
    const { data } = await api.get("/rates");
    return { eurToTl: data.eurToTl || 38, usdToTl: data.usdToTl || 34, tcmbDate: data.tcmbDate };
  } catch (e) { return { eurToTl: 38, usdToTl: 34, tcmbDate: "-" }; }
}

export function convAllCurrencies(amount, fromCur, rates) {
  const eurTl = rates.eurToTl, usdTl = rates.usdToTl;
  let tl = Number(amount) || 0;
  if (fromCur === "EUR") tl = tl * eurTl;
  else if (fromCur === "USD") tl = tl * usdTl;
  return { tl, eur: tl / eurTl, usd: tl / usdTl };
}

export function fmtPrint(n, cur = "TL") {
  const code = { TL: "TL", EUR: "EUR", USD: "USD" }[cur] || cur;
  return `${Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${code}`;
}

// ── Eksik Alan Güvenliği ─────────────────────────────────────────────────────
// Ölçüsüz / tanımsız değerler PDF'e ASLA "undefined" olarak sızmamalı → "—" yazılır.
const dash = (v) =>
  (v === undefined || v === null || v === "" || (typeof v === "number" && !Number.isFinite(v)) ? "—" : v);
// Pozitif sayı beklenen alanlar (en / boy / adet): 0 veya eksik → "—"
const numDash = (v) => (Number(v) > 0 ? v : "—");
// "150 cm" biçimi; ölçüsüz kalemde → "—"
const cmText = (v) => (Number(v) > 0 ? `${v} cm` : "—");

export function whatsappShare(phone, message) {
  const clean = (phone || "").replace(/[^\d]/g, "");
  const url = `https://wa.me/${clean}?text=${encodeURIComponent(message)}`;
  window.open(url, "_blank");
}

// ── E-posta Şablonları (Ekstre / Teklif) ──────────────────────────────────────
export const DEFAULT_EMAIL_TEMPLATES = {
  statement_email: {
    label: "Ekstre E-postası",
    subject: "ZEN Cari Ekstre — {{musteri_adi}}",
    body: "Sayın {{musteri_adi}},\n\nGüncel cari hesap ekstrenizi ekte PDF olarak bulabilirsiniz.\nGüncel bakiye: {{bakiye}}\n\n{{firma_adi}}",
  },
  quote_email: {
    label: "Teklif E-postası",
    subject: "ZEN Teklif #{{teklif_no}} — {{musteri_adi}}",
    body: "Sayın {{musteri_adi}},\n\nTalebiniz üzerine hazırladığımız #{{teklif_no}} numaralı teklifimizi ekte PDF olarak bulabilirsiniz.\nToplam Tutar: {{toplam}}\n\n{{firma_adi}}",
  },
};

export function renderEmailTemplate(tpl, vars) {
  const fill = (s) => String(s || "").replace(/\{\{\s*(\w+)\s*\}\}/g, (m, k) => (vars[k] !== undefined ? vars[k] : m));
  return { subject: fill(tpl.subject), body: fill(tpl.body) };
}

export async function fetchEmailTemplate(key) {
  try {
    const { data } = await api.get("/settings", { params: { category: "email_templates" } });
    const row = (data || []).find((r) => r.key === key);
    if (row?.value?.subject) return row.value;
  } catch (e) { /* varsayılana düş */ }
  return DEFAULT_EMAIL_TEMPLATES[key];
}

export function apiErrorMessage(e, fallback) {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || JSON.stringify(d)).join(", ") || fallback;
  if (typeof detail === "string") return detail;
  return e?.message || fallback;
}

// ── Gönderim Geçmişi (e-posta / WhatsApp log) ─────────────────────────────────
export async function logSend({ refType, refId, refLabel, channel, to, subject, status = "sent", error = null }) {
  try {
    await api.post("/send-logs", { refType, refId, refLabel, channel, to, subject, status, error });
  } catch (e) { console.warn("logSend başarısız:", e?.response?.data?.detail || e.message); }
}

// ── Unicode Font Yükleme (NotoSans base64 gömülü — Türkçe karakter garantili) ─
// Kural: jsPDF dokümanı üretirken ASLA ASCII dönüşümü (trAsciiSafe) yapma.
//        Her zaman bu applyUnicodeFont() veya newTurkishPdf() kullan.

let _fontModule = null;

// Güvenli ASCII eşleme — YALNIZCA Unicode font gömülemezse (fallback) kullanılır.
// jsPDF standart fontları metni Latin-1'e indirger (İ→0, Ş→^, ş→_ gibi bozulma);
// bu yüzden Türkçe karakterler doğru ASCII karşılıklarına çevrilir:
// İ→I, Ş→S, Ğ→G, ı→i, ş→s, ğ→g, Ç→C, Ö→O, Ü→U — asla 0, ^, _ gibi karakterlere değil.
const TR_ASCII_MAP = {
  "ı": "i", "İ": "I", "ş": "s", "Ş": "S", "ğ": "g", "Ğ": "G",
  "₺": "TL", "€": "EUR", "→": "->", "←": "<-", "—": "-", "–": "-", "…": "...",
};
export function trAsciiSafe(text) {
  if (text === undefined || text === null) return "";
  return String(text)
    .replace(/[ıİşŞğĞ₺€→←—–…]/g, (c) => TR_ASCII_MAP[c] || c)
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")   // ç→c, ö→o, ü→u, â→a, î→i
    .replace(/[^\t\n\r\x20-\xFF]/g, "");                // Latin-1'e sığmayan kalanlar atılır
}

// Font gömülemezse son çare: doc.text çıktılarını güvenli ASCII'ye çevirir.
// (autoTable da aynı doc üzerinden yazdığı için tablo metinleri de korunur.)
function installAsciiFallback(doc) {
  if (doc.__zenAsciiFallback) return;
  const origText = doc.text.bind(doc);
  doc.text = (...args) => {
    if (typeof args[0] === "string") args[0] = trAsciiSafe(args[0]);
    else if (Array.isArray(args[0])) args[0] = args[0].map((t) => (typeof t === "string" ? trAsciiSafe(t) : t));
    return origText(...args);
  };
  doc.__zenAsciiFallback = true;
}

async function applyUnicodeFont(doc) {
  // Aynı dokümanda (ör. addPage sonrası) font tekrar parse edilmesin — performans
  if (doc.__zenUnicodeFontReady) { doc.setFont("NotoSans", "normal"); return; }
  try {
    if (!_fontModule) {
      _fontModule = await import("./notoSansFont");
    }
    const { NOTO_SANS_REGULAR_B64, NOTO_SANS_BOLD_B64 } = _fontModule;
    doc.addFileToVFS("NotoSans-Regular.ttf", NOTO_SANS_REGULAR_B64);
    doc.addFont("NotoSans-Regular.ttf", "NotoSans", "normal");
    doc.addFileToVFS("NotoSans-Bold.ttf", NOTO_SANS_BOLD_B64);
    doc.addFont("NotoSans-Bold.ttf", "NotoSans", "bold");
    doc.setFont("NotoSans", "normal");
    doc.__zenUnicodeFontReady = true;
  } catch (e) {
    console.warn("[PDF] Unicode font kaydedilemedi — ASCII fallback devrede:", e);
    installAsciiFallback(doc);
  }
}

/**
 * Unicode destekli (NotoSans) yeni bir jsPDF belgesi oluşturur.
 * Tüm yeni PDF oluşturucular bu factory'yi kullanmalıdır.
 * Kural: ASCII dönüşümü YASAK — orijinal Türkçe / Unicode metni doğrudan yaz.
 */
export async function newTurkishPdf(opts = {}) {
  const { unit = "mm", format = "a4", orientation = "portrait", ...rest } = opts;
  const doc = new jsPDF({ unit, format, orientation, ...rest });
  await applyUnicodeFont(doc);
  return doc;
}
const bi = (en, tr) => (MONEY.lang === "en" ? `${en} (${tr})` : tr);
const hb = (en, tr) => (MONEY.lang === "en" ? `${en} (${tr})` : tr);
const pdfProduct = (name) => {
  const t = name || "-";
  if (MONEY.lang !== "en" || !name) return t;
  const en = productEn(name);
  return en === t ? t : `${en} (${t})`;
};
const htmlProduct = (name) => {
  const t = String(name || "-");
  if (MONEY.lang !== "en" || !name) return t;
  const en = productEn(name);
  return en === t ? t : `${en} <span style="font-size:9px;color:#777">(${t})</span>`;
};
const pdfMoney = (v, cur) => `${Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur || "TL"}`;

// ── Perfore lamel CSS arka plan stili ─────────────────────────────────────────
export function slatSwatchStyle(hexColor, label) {
  const base = hexColor || "#CCCCCC";
  const isPerf = (label || "").toLowerCase().includes("perfore");
  if (!isPerf) return { background: base };
  const lum = parseInt(base.replace("#", "").slice(0, 6), 16);
  const hole = lum < 0x888888 ? "rgba(255,255,255,0.32)" : "rgba(0,0,0,0.28)";
  return { background: base, backgroundImage: `radial-gradient(circle, ${hole} 35%, transparent 37%)`, backgroundSize: "5px 5px" };
}

// ── Teklif Satırları Genişletici ─────────────────────────────────────────────
export function expandQuoteRows(items) {
  const rows = [];
  for (const it of (items || [])) {
    const motorTot = Number(it.motorTotalInCur) || 0;
    const extrasDef = GROUP_EXTRAS[it.groupSlug];
    const extraKeys = Array.isArray(it.pliseExtras) ? it.pliseExtras : [];
    const extraRows = [];
    let extrasSum = 0;
    if (extrasDef && extraKeys.length > 0) {
      const m2 = Number(it.computedM2) || 0;
      const qty = Number(it.qty) || 1;
      for (const key of extraKeys) {
        const def = extrasDef.find(d => d.key === key);
        if (!def) continue;
        let itemTotal = def.type === "m2" ? def.price * m2 * qty : def.type === "adet" ? def.price * qty : 0;
        if (itemTotal <= 0) continue;
        extrasSum += itemTotal;
        extraRows.push({ product: def.label, isExtraRow: true, qty, unitPrice: itemTotal / qty, kdvRate: it.kdvRate ?? 20, lineTotal: itemTotal, currency: it.currency, widthCm: 0, heightCm: 0 });
      }
    }
    const baseTotal = Math.max(0, (Number(it.lineTotal) || 0) - (it.motor && motorTot > 0 ? motorTot : 0) - extrasSum);
    // Motor/ekstra satırları ayrıldığında birim fiyat da kalan tutara göre
    // yeniden hesaplanmalı; aksi halde "B.Fiyat × Adet ≠ Tutar" oluyordu (K4).
    const baseQty = Math.max(1, Number(it.qty) || 1);
    const baseItem = (extraRows.length > 0 || (it.motor && motorTot > 0))
      ? { ...it, lineTotal: baseTotal, unitPrice: baseTotal / baseQty }
      : it;
    rows.push(baseItem);
    extraRows.forEach(r => rows.push(r));
    if (it.motor && motorTot > 0) {
      rows.push({ product: `Motor — ${it.motorLabel || "Standart"}`, isMotorRow: true, qty: it.qty || 1, unitPrice: motorTot / (it.qty || 1), kdvRate: it.kdvRate ?? 20, lineTotal: motorTot, currency: it.currency, widthCm: 0, heightCm: 0, controlSide: it.controlSide });
    }
  }
  return rows;
}

export function quoteItemDesc(it) {
  if (it.isMotorRow) return it.controlSide ? `Motor Yönü: ${it.controlSide}` : "";
  const parts = [];
  // Tül & Fon: pile oranı + kumaş eni + birim fiyat birimi
  if (it.groupSlug === "tul_fon_perde") {
    if (it.pileSikligi) parts.push(`Pile x${it.pileSikligi}`);
    if (it.kumasEniCm)  parts.push(`${it.kumasEniCm} cm eni`);
    const kanat = kanatChoice(it) === "iki" ? "İki Kanat" : "Tek Kanat";
    parts.push(kanat);
    if (it.unitPrice > 0) parts.push(`${Number(it.unitPrice).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ${it.currency || "TL"}/mtül`);
    if (it.astarli && it.astarKumas) {
      const astarCode = it.astarFabricCode ? ` (${it.astarFabricCode})` : "";
      parts.push(`Astar: ${it.astarKumas}${astarCode} - impex`);
    }
    return parts.join(" · ");
  }
  const zin = it.zincirTipi || it.zincir;
  if (!it.motor && zin) parts.push(/zincir/i.test(zin) ? `${zin}li — Manuel` : `${zin} Zincirli — Manuel`);
  if (it.motor) parts.push(`Motorlu${it.motorLabel ? ` (${it.motorLabel})` : ""}`);
  const kasa = it.kasa || it.kasaTipi;
  if (kasa) parts.push(/kasa/i.test(kasa) ? kasa : `${kasa} Kasa`);
  if (it.motor && zin) parts.push(/zincir/i.test(zin) ? zin : `${zin} Zincir`);
  if (it.controlSide) parts.push(it.motor ? `Motor Yönü: ${it.controlSide}` : `Zincir Yönü: ${it.controlSide}`);
  if (it.mekanizma) parts.push(`Mekanizma: ${it.mekanizma}`);
  return parts.join(" · ");
}

export function fullItemDesc(it) {
  const parts = [];
  if (it.zipModeli) parts.push(`Model: ${it.zipModeli}`);
  if (it.zipCesidi) parts.push(`ZIP: ${it.zipCesidi}`);
  if (it.fabricBrand) parts.push(`Kumaş Çeşidi: ${it.fabricBrand}`);
  if (it.motor) parts.push(`Motor: ${it.motorLabel || "Motorlu"}`);
  if (it.controlLabel) parts.push(`Kumanda: ${it.controlLabel}`);
  else if (it.control) parts.push(`Kumanda: ${it.control}`);
  const zin = it.zincirTipi || it.zincir;
  if (!it.motor && zin) parts.push(/zincir/i.test(zin) ? `${zin}li Manuel` : `${zin} Zincirli Manuel`);
  const kasa = it.kasa || it.kasaTipi;
  if (kasa) parts.push(/kasa/i.test(kasa) ? kasa : `${kasa} Kasa`);
  if (it.kasaRengi) parts.push(`Kasa Rengi: ${it.kasaRengiLabel || it.kasaRengi}`);
  if (it.controlSide) parts.push(it.motor ? `Motor Yönü: ${it.controlSide}` : `Zincir Yönü: ${it.controlSide}`);
  if (it.mekanizma) parts.push(`Mekanizma: ${it.mekanizma}`);
  if (it.renkTipi === "boyali") parts.push(`Boyalı (RAL ${it.ralCode || "-"}) · +${SINEKLIK_BOYALI_KABIN_UCRETI.toLocaleString("tr-TR")} TL Kabin`);
  if (it.renkTipi === "ahsap_desenli") parts.push(`Ahşap Desenli: ${it.ahsapRenk || "-"} · +%${SINEKLIK_AHSAP_DESEN_PCT}`);
  return parts;
}

/** İmalat Fişi: Bir kalem için doldurulmuş TÜM alanları "Etiket: Değer" formatında döndürür */
export function buildItemAllFields(it) {
  const p = [];

  // ── Kumaş ──────────────────────────────────────────────────────────────────
  if (it.fabric)     p.push(`Kumaş: ${it.fabric}`);
  if (it.fabricCode && it.fabricCode !== it.fabric) p.push(`Kumaş Kodu: ${it.fabricCode}`);
  if (it.fabricBrand) p.push(`Kumaş Çeşidi: ${it.fabricBrand}`);
  if (it.fabric2)    p.push(`2. Kumaş: ${it.fabric2}`);
  if (it.fabricCode2 && it.fabricCode2 !== it.fabric2) p.push(`2. Kumaş Kodu: ${it.fabricCode2}`);
  if (it.nattySeri)  p.push(`Seri: ${it.nattySeri}`);
  if (it.system14)   p.push(`Sistem: ${it.system14}`);

  // ── Renk / Slat ────────────────────────────────────────────────────────────
  if (it.ralCode)      p.push(`RAL Renk: ${it.ralCode}`);
  if (it.slatSize)     p.push(`Slat: ${it.slatSize}mm${it.slatColorCode ? ` / ${it.slatColorCode}` : ""}`);
  else if (it.slatColorCode) p.push(`Slat Rengi: ${it.slatColorCode}`);
  if (it.dikeyTipi)   p.push(`Dikey Tipi: ${it.dikeyTipi}`);

  // ── ZIP ────────────────────────────────────────────────────────────────────
  if (it.zipModeli)   p.push(`Model: ${it.zipModeli}`);
  if (it.zipCesidi)   p.push(`ZIP Çeşidi: ${it.zipCesidi}`);

  // ── Mekanizma / Kasa ───────────────────────────────────────────────────────
  if (it.mekanizma)       p.push(`Mekanizma: ${it.mekanizma}`);
  if (it.mekanizmaCesidi) p.push(`Mek. Çeşidi: ${it.mekanizmaCesidi}`);
  const kasa = it.kasa || it.kasaTipi;
  if (kasa) p.push(/kasa/i.test(kasa) ? `Kasa Tipi: ${kasa}` : `Kasa: ${kasa}`);
  if (it.kasaRengi) p.push(`Kasa Rengi: ${it.kasaRengiLabel || it.kasaRengi}`);

  // ── Motor / Kumanda / Yön ──────────────────────────────────────────────────
  if (it.motor) {
    p.push(`Motor: ${it.motorLabel || "Motorlu"}`);
    if (it.controlLabel)       p.push(`Kumanda: ${it.controlLabel}`);
    else if (it.control)       p.push(`Kumanda: ${it.control}`);
    if (it.controlSide)        p.push(`Motor Yönü: ${it.controlSide}`);
  } else {
    const zin = it.zincirTipi || it.zincir;
    if (zin)           p.push(`Kumanda: ${/zincir/i.test(zin) ? `${zin}li Manuel` : `${zin} Zincirli Manuel`}`);
    if (it.controlSide) p.push(`Zincir Yönü: ${it.controlSide}`);
    if (it.controlLabel) p.push(`Kumanda Tipi: ${it.controlLabel}`);
    else if (it.control) p.push(`Kumanda Tipi: ${it.control}`);
  }

  // ── Montaj ─────────────────────────────────────────────────────────────────
  if (it.mounting)  p.push(`Montaj Tipi: ${it.mounting}`);
  if (it.cableDir)  p.push(`Yön: ${it.cableDir}`);
  if (it.midFoot)   p.push(`Montaj Ayağı: ${it.midFoot === true ? "Var" : it.midFoot}`);

  // ── Rustik özel ────────────────────────────────────────────────────────────
  if (it.rustikTipi) {
    const tipMap = { kanalli: "Alüminyum Kanallı", ahsap: "Ahşap Rustik", antik: "Antik & Retro" };
    p.push(`Rustik Tipi: ${tipMap[it.rustikTipi] || it.rustikTipi}`);
    if (it.rustikTipi === "kanalli" && it.kanallıBaşlıkCesidi) p.push(`Başlık: ${it.kanallıBaşlıkCesidi}`);
    if (it.rustikTipi === "ahsap"   && it.ahsapRenk)           p.push(`Renk: ${it.ahsapRenk}`);
    if (it.rustikTipi === "antik"   && it.antikBoruCapi)        p.push(`Çap: ${it.antikBoruCapi}`);
    if (it.rayOlsunMu) p.push("Ray Dahil");
  }

  // ── Ekstra seçenekler ──────────────────────────────────────────────────────
  if (it.extrasLabel) p.push(`Ekstra: ${it.extrasLabel}`);

  // ── Oda / Not ──────────────────────────────────────────────────────────────
  if (it.roomName) p.push(`Oda: ${it.roomName}`);
  if (it.note)     p.push(`Not: ${it.note}`);

  return p.filter(Boolean);
}

// ── HTML Quote Builder ─────────────────────────────────────────────────────────
export function buildQuoteHTML(order, rates, logoUrl) {
  const cur = order.currency || "TL";
  const rows = expandQuoteRows(order.items).map((it, i) => {
    const isTulFonRow = it.groupSlug === "tul_fon_perde";
    const m2Val = isTulFonRow
      ? (it.computedM2 > 0 ? `${Number(it.computedM2).toFixed(2)} mtül` : "-")
      : (it.computedM2 > 0 ? Number(it.computedM2).toFixed(2) : (it.widthCm && it.heightCm ? ((it.widthCm / 100) * (it.heightCm / 100)).toFixed(2) : "-"));
    const fabricCell = (it.isExtraRow || it.isMotorRow) ? "—" : ((it.fabric || (it.slatSize ? `${it.slatSize}mm ${it.slatColorCode || ""}`.trim() : "-")).replace(/</g, "&lt;"));
    const rowStyle = (it.isExtraRow || it.isMotorRow) ? ' style="background:#F8FAFF;color:#444"' : "";
    return `<tr${rowStyle}>
      <td class="c b">${i + 1}</td>
      <td>${htmlProduct((it.product || "-").replace(/</g, "&lt;"))}${quoteItemDesc(it) ? `<div style="font-size:9px;color:#666;margin-top:2px">${quoteItemDesc(it).replace(/</g, "&lt;")}</div>` : ""}</td>
      <td>${fabricCell}</td>
      <td class="c">${it.widthCm > 0 ? it.widthCm : "—"}</td>
      <td class="c">${it.heightCm > 0 ? it.heightCm : "—"}</td>
      <td class="c">${m2Val}</td>
      <td class="c">${it.qty || 1}</td>
      <td class="r">${fmtPrint((Number(it.unitPrice) > 0 ? it.unitPrice : (it.lineTotal / Math.max(1, it.qty || 1))), it.currency || cur)}</td>
      <td class="c">%${it.kdvRate ?? 20}</td>
      <td class="r b">${fmtPrint(it.lineTotal, it.currency || cur)}</td>
    </tr>`;
  }).join("");

  const breakdown = (order.vatBreakdown || []).slice().sort((a, b) => a.rate - b.rate);
  const vatRows = breakdown.map(g => `
    <tr class="t-row"><td colspan="8" class="r">${hb("VAT", "KDV")} %${g.rate} (net ${fmtPrint(g.net, cur)}):</td><td colspan="2" class="r">${fmtPrint(g.kdv, cur)}</td></tr>`).join("");

  const totalConv = convAllCurrencies(order.total, cur, rates);
  const vatTotal = (order.vatBreakdown || []).reduce((s, g) => s + (g.kdv || 0), 0) || order.vatAmount || 0;
  const netTotal = (order.total || 0) - vatTotal;
  const netConv = convAllCurrencies(netTotal, cur, rates);
  const CUR_NAMES = MONEY.lang === "en" ? { TL: "Turkish Lira", EUR: "Euro", USD: "US Dollar" } : { TL: "Türk Lirası", EUR: "Euro", USD: "ABD Doları" };
  const CONV_KEY = { TL: "tl", EUR: "eur", USD: "usd" };
  const otherCurs = ["TL", "EUR", "USD"].filter(c => c !== cur);

  return `
<!DOCTYPE html><html lang="tr"><head><meta charset="utf-8"><meta name="color-scheme" content="light"><title>ZEN Teklif ${ono(order)}</title>
<style>
  @page { size: A4 portrait; margin: 14mm 12mm; }
  * { box-sizing: border-box; }
  html { background: #ffffff; color-scheme: light; }
  body { font-family: 'Segoe UI', 'Roboto', Arial, sans-serif; color: #1a1a1a; background: #ffffff; margin: 0; font-size: 11.5px; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 12px; border-bottom: 2px solid #E5322A; }
  .header .logo img { height: 46px; object-fit: contain; }
  .header .comp { text-align: right; font-size: 10.5px; line-height: 1.5; color: #444; }
  .header .comp b { color: #111; }
  .title { margin: 18px 0 4px; }
  .title h1 { margin: 0; font-size: 22px; font-weight: 800; letter-spacing: 3px; color: #E5322A; }
  .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; margin: 8px 0 16px; padding: 10px 14px; background: #F5F5F7; border: 1px solid #E5E5EA; border-radius: 6px; }
  .meta .row { display: flex; gap: 10px; font-size: 11.5px; }
  .meta .k { min-width: 110px; font-weight: 700; color: #333; }
  .meta .v { color: #111; font-weight: 500; }
  table.plist { width: 100%; border-collapse: collapse; margin-top: 6px; font-size: 11px; }
  table.plist th { background: #E5322A; color: #fff; text-align: center; padding: 7px 6px; border: 1px solid #B0221D; font-weight: 700; font-size: 10.5px; }
  table.plist td { border: 1px solid #C8C8CE; padding: 7px 6px; vertical-align: middle; }
  table.plist tr:nth-child(even) td { background: #FAFAFB; }
  td.c { text-align: center; } td.r { text-align: right; } td.b { font-weight: 700; }
  tr.t-row td { border: 0; padding: 3px 6px; font-size: 11px; background: transparent !important; }
  .grand { background: #1E293B; color: #fff; padding: 10px 14px; display: flex; justify-content: space-between; align-items: center; margin-top: 4px; border-radius: 4px; font-size: 14px; font-weight: 800; letter-spacing: 1px; }
  .conv-box { margin-top: 12px; padding: 12px 14px; background: #FFF7ED; border: 1px dashed #FB923C; border-radius: 6px; font-size: 11px; }
  .conv-box .head { font-size: 10px; text-transform: uppercase; letter-spacing: 1.5px; color: #9A3412; font-weight: 700; margin-bottom: 6px; }
  .conv-box .row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
  .conv-box .row div { display: flex; justify-content: space-between; padding: 6px 8px; background: #fff; border-radius: 4px; border: 1px solid #FED7AA; }
  .conv-box .row div b { color: #9A3412; }
  .footer { position: fixed; bottom: 12mm; left: 12mm; right: 12mm; font-size: 9px; color: #888; padding-top: 6px; border-top: 1px solid #E5E5EA; display: flex; justify-content: space-between; }
</style></head>
<body>
  <div class="header">
    <div class="logo">${logoUrl ? `<img src="${logoUrl}" alt="ZEN">` : `<h2 style="margin:0;color:#E5322A">ZEN</h2>`}</div>
    <div class="comp"><b>ZEN Mekanizmalı Perde Sistemleri</b><br>info@zenperde.com · www.zenperde.com<br>TCMB Kur: 1 EUR = ₺${rates.eurToTl.toFixed(2)} · 1 USD = ₺${rates.usdToTl.toFixed(2)}</div>
  </div>
  <div class="title"><h1>${hb("QUOTE / ORDER", "TEKLİF / SİPARİŞ")}</h1></div>
  <div class="meta">
    <div class="row"><div class="k">${hb("Document No", "Belge No")}</div><div class="v">${(ono(order) || "-").replace(/</g, "&lt;")}</div></div>
    <div class="row"><div class="k">${hb("Customer", "Müşteri")}</div><div class="v">${(order.customerName || "-").replace(/</g, "&lt;")}</div></div>
    <div class="row"><div class="k">${hb("Date", "Tarih")}</div><div class="v">${new Date(order.createdAt || Date.now()).toLocaleDateString("tr-TR")}</div></div>
    <div class="row"><div class="k">${hb("Project / Job", "Proje / İş")}</div><div class="v">${(order.projectName || "-").replace(/</g, "&lt;")}</div></div>
    <div class="row"><div class="k">${hb("Due Date", "Termin")}</div><div class="v">${order.deadline ? fmtDate(order.deadline) : "-"}</div></div>
    <div class="row"><div class="k">${hb("Payment", "Ödeme")}</div><div class="v">${order.paymentMode === "kredi_karti" ? hb("Credit Card", "Kredi Kartı") : hb("Cash / Transfer", "Nakit / Havale")}</div></div>
  </div>
  <table class="plist">
    <thead><tr>
      <th style="width:4%">#</th><th style="width:20%">${hb("Product", "Ürün")}</th><th style="width:16%">${hb("Fabric", "Kumaş/Renk")}</th>
      <th style="width:7%">${hb("Width", "En")}<br>(cm)</th><th style="width:7%">${hb("Height", "Boy")}<br>(cm)</th>
      <th style="width:6%">m²/mtül</th><th style="width:5%">${hb("Qty", "Adet")}</th>
      <th style="width:12%">${hb("Unit Price", "B.Fiyat")}</th><th style="width:6%">${hb("VAT", "KDV")}</th><th style="width:13%">${hb("Amount", "Tutar")}</th>
    </tr></thead>
    <tbody>
      ${rows}
      <tr class="t-row"><td colspan="8" class="r">${hb("Subtotal", "Ara Toplam")}:</td><td colspan="2" class="r b">${fmtPrint(order.subtotal, cur)}</td></tr>
      ${order.discountPercent ? `<tr class="t-row"><td colspan="8" class="r">${hb("Discount", "İndirim")} (${order.discountPercent}%):</td><td colspan="2" class="r" style="color:#dc2626">- ${fmtPrint((order.subtotal * order.discountPercent) / 100, cur)}</td></tr>` : ""}
      ${vatRows}
    </tbody>
  </table>
  <div class="grand">
    <span>GENEL TOPLAM · TOTAL</span>
    <span style="text-align:right">
      ${fmtPrint(order.total, cur)}
      <span style="display:block;font-size:10.5px;font-weight:600;opacity:.85;margin-top:2px">≈ ${otherCurs.map(c => fmtPrint(totalConv[CONV_KEY[c]], c)).join(" · ")}</span>
    </span>
  </div>
  <div class="conv-box">
    <div class="head">📊 ${hb("Other Amounts", "Diğer Tutarlar")}</div>
    <table style="width:100%;border-collapse:collapse;font-size:10.5px">
      <tr>
        <td style="padding:4px 8px;color:#9A3412;font-weight:700"></td>
        <td style="padding:4px 8px;text-align:right;color:#9A3412;font-weight:700">${hb("Excl. VAT", "KDV Hariç")}</td>
        <td style="padding:4px 8px;text-align:right;color:#9A3412;font-weight:700">${hb("Incl. VAT", "KDV Dahil")}</td>
      </tr>
      ${otherCurs.map(c => `
      <tr style="background:#fff">
        <td style="padding:6px 8px;border:1px solid #FED7AA;font-weight:600">${CUR_NAMES[c]} (${c})</td>
        <td style="padding:6px 8px;border:1px solid #FED7AA;text-align:right">${fmtPrint(netConv[CONV_KEY[c]], c)}</td>
        <td style="padding:6px 8px;border:1px solid #FED7AA;text-align:right;font-weight:800;color:#9A3412">${fmtPrint(totalConv[CONV_KEY[c]], c)}</td>
      </tr>`).join("")}
    </table>
    <div style="margin-top:6px;font-size:9.5px;color:#9A3412">
      ${MONEY.lang === "en" ? `Based on the CBRT daily rate (${rates.tcmbDate || new Date().toLocaleDateString("en-GB")}). The rate at the invoicing date may apply.` : `TCMB günlük kuru referans alınmıştır (${rates.tcmbDate || new Date().toLocaleDateString("tr-TR")}). Fatura kesim tarihindeki güncel kur uygulanabilir.`}
    </div>
  </div>
  ${order.notes ? `<div style="margin-top:14px;padding:10px 12px;background:#F5F5F7;border-radius:6px;font-size:10.5px"><b>${hb("Notes", "Notlar")}:</b> ${(order.notes || "").replace(/</g, "&lt;")}</div>` : ""}
  <div class="footer">
    <span>${hb("Quote valid for 7 days", "Teklif 7 gün geçerlidir")} · ZEN Mekanizmalı Perde Sistemleri</span>
    <span>www.zenperde.com · info@zenperde.com</span>
  </div>
</body></html>`;
}

export async function generateQuotePDF(order) {
  const logoUrl = await loadLogoDataUrl();
  const rates = await fetchRates();
  const html = buildQuoteHTML(order, rates, logoUrl);
  printViaIframe(html, false);
  toast.success("Yazdırma diyalogu açılıyor…");
}

// ── Kargo Etiketi HTML ────────────────────────────────────────────────────────
export function buildCargoLabelHTML(order, opts = {}) {
  const w = opts.w || 100;
  const h = opts.h || 75;
  const fontSize = opts.fontSize || 9;
  const showBorder = opts.showBorder !== false;
  const orderNo = `SP-${String(order.orderNo || order.id || "").replace("ORD-", "").slice(0, 8)}`;
  const recipient = order.shipToName || order.customerName || "—";
  const address = order.shippingAddress || order.projectName || "";
  const carrier = order.shippingInfo?.carrier || "";
  const trackingNo = order.shippingInfo?.trackingNo || "";
  const today = new Date().toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });

  return `<!DOCTYPE html><html lang="tr"><head><meta charset="utf-8"><meta name="color-scheme" content="light">
<title>Kargo Etiketi ${orderNo}</title>
<style>
  @page { size: ${w}mm ${h}mm; margin: 0; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; }
  html, body { width: ${w}mm; height: ${h}mm; background: #fff; color: #000; font-family: Arial, Helvetica, sans-serif; font-size: ${fontSize}pt; margin: 0; padding: 0; overflow: hidden; }
  .wrap { width: ${w}mm; height: ${h}mm; padding: 3mm 3mm 2mm 3mm; display: flex; flex-direction: column; ${showBorder ? "border: 1px solid #000;" : ""} }
  .hdr { display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #555; padding-bottom: 1.5mm; margin-bottom: 1.5mm; }
  .co-name { font-size: ${fontSize + 1}pt; font-weight: 900; letter-spacing: .03em; color: #E5322A; }
  .label-tag { font-size: ${Math.max(6, fontSize - 1)}pt; border: 1px solid #555; padding: 0.5mm 1.5mm; border-radius: 1mm; font-weight: 700; color: #333; }
  .section-lbl { font-size: ${Math.max(6, fontSize - 2)}pt; color: #888; text-transform: uppercase; letter-spacing: .05em; margin-bottom: 0.5mm; font-weight: 700; }
  .recipient { font-size: ${fontSize + 3}pt; font-weight: 900; line-height: 1.2; margin: 1mm 0; }
  .address { font-size: ${fontSize}pt; color: #444; line-height: 1.3; flex: 1; }
  .divider { border: none; border-top: 0.5px solid #ccc; margin: 1.5mm 0; }
  .footer { display: flex; justify-content: space-between; align-items: flex-end; margin-top: auto; }
  .order-no { font-family: 'Courier New', monospace; font-size: ${fontSize + 2}pt; font-weight: 900; color: #E5322A; letter-spacing: .06em; }
  .carrier-box { text-align: right; font-size: ${Math.max(6, fontSize - 1)}pt; color: #555; }
  .barcode-row { text-align: center; margin: 0.5mm 0; }
  .barcode-line { font-family: 'Courier New', monospace; font-size: 5pt; letter-spacing: 2px; }
</style></head><body>
<div class="wrap">
  <div class="hdr">
    <div class="co-name">ZEN PERDE</div>
    <div class="label-tag">KARGO</div>
  </div>
  <div class="section-lbl">Alıcı</div>
  <div class="recipient">${String(recipient).replace(/</g, "&lt;").slice(0, 35)}</div>
  ${address ? `<div class="address">${String(address).replace(/</g, "&lt;").slice(0, 80)}</div>` : ""}
  <hr class="divider">
  <div class="footer">
    <div>
      <div class="section-lbl">Sipariş No</div>
      <div class="order-no">${orderNo}</div>
      ${trackingNo ? `<div class="section-lbl" style="margin-top:1mm">Takip No</div><div style="font-family:monospace;font-size:${Math.max(6, fontSize - 1)}pt;font-weight:700">${trackingNo}</div>` : ""}
    </div>
    <div class="carrier-box">
      ${carrier ? `<div style="font-weight:900;font-size:${fontSize + 1}pt">${carrier}</div>` : ""}
      <div>${today}</div>
    </div>
  </div>
</div>
</body></html>`;
}

// ── Tül/Fon Şema Yardımcı: Mühendislik Tipi Ölçü Çizgisi ────────────────────
function _dimLine(doc, x1, y1, x2, y2, label) {
  const TICK = 1.8; // yarım tik uzunluğu (mm)
  const isH  = Math.abs(y2 - y1) < 0.1;
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.2);
  doc.line(x1, y1, x2, y2);
  if (isH) {
    doc.line(x1, y1 - TICK, x1, y1 + TICK);
    doc.line(x2, y2 - TICK, x2, y2 + TICK);
    const midX = (x1 + x2) / 2;
    doc.setFontSize(7.5);
    doc.setFont("NotoSans", "normal");
    doc.setTextColor(0, 0, 0);
    const tw = doc.getTextWidth(label);
    doc.setFillColor(255, 255, 255);
    doc.rect(midX - tw / 2 - 0.8, y1 - 5.0, tw + 1.6, 4.4, "F");
    doc.text(label, midX, y1 - 1.2, { align: "center" });
  } else {
    doc.line(x1 - TICK, y1, x1 + TICK, y1);
    doc.line(x2 - TICK, y2, x2 + TICK, y2);
    const midY = (y1 + y2) / 2;
    doc.setFontSize(7.5);
    doc.setFont("NotoSans", "normal");
    doc.setTextColor(0, 0, 0);
    doc.text(label, x1 - 3.5, midY, { angle: 90, align: "center" });
  }
}

// ── Tül/Fon Terzi Föyü — tek çizim motoru, önizleme / indirme / baskı ──
export async function buildTulFonSchemaDoc(order) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  await applyUnicodeFont(doc);
  const logoUrl = await loadLogoDataUrl();
  return renderTailorSheet(doc, order, logoUrl);
}




export async function generateTulFonSchemaPDF(order) {
  try {
    const doc = await buildTulFonSchemaDoc(order);
    doc.save(`ZEN-Terzi-Foyu-${order.orderNo || order.quoteNo || order.id}.pdf`);
    toast.success("Terzi Föyü indirildi");
  } catch (e) {
    toast.error("PDF oluşturulamadı: " + (e?.message || e));
  }
}

// ── jsPDF Belgeleri ───────────────────────────────────────────────────────────
// Tül/Fon imalat kartı — şema + ölçü oklarıyla birlikte (inline)
function _drawTulFonCard(doc, it, num, startY, pageW) {
  const wCm   = Number(it.widthCm)  || 0;
  const hCm   = Number(it.heightCm) || 0;
  const isIki = kanatChoice(it) === "iki";
  const qty   = Number(it.qty) || 1;
  const pile  = Number(it.pileSikligi) || 0;
  const wingW = isIki ? wCm / 2 : wCm;
  const LX    = 14;
  const CW    = pageW || 182;

  // ── Başlık bandı ───────────────────────────────────────────────────────────
  doc.setFillColor(22, 22, 22);
  doc.rect(LX, startY, CW, 8, "F");
  doc.setFont("NotoSans", "bold"); doc.setFontSize(9); doc.setTextColor(255, 255, 255);
  doc.text(`${num}.  ${it.product || "Tül & Fon Perde"}`, LX + 3, startY + 5.5);
  doc.setFont("NotoSans", "normal"); doc.setFontSize(7.5); doc.setTextColor(190, 190, 190);
  doc.text(`${qty} adet  ·  ${isIki ? "Çift Kanat" : "Tek Kanat"}`, LX + CW - 3, startY + 5.5, { align: "right" });

  let y = startY + 8;

  // ── Detay satırı ─────────────────────────────────────────────────────────
  doc.setFillColor(244, 246, 250);
  doc.rect(LX, y, CW, 9, "F");
  doc.setFont("NotoSans", "normal"); doc.setFontSize(8); doc.setTextColor(35, 35, 35);
  const dp = [
    it.fabric         && `Kumaş: ${it.fabric}`,
    pile              && `Pile ×${pile}`,
    it.kumasEniCm     && `Kumaş Eni: ${it.kumasEniCm} cm`,
    it.computedM2     && `${Number(it.computedM2).toFixed(2)} mtül/adet`,
    (it.astarli && it.astarKumas) && `Astar: ${it.astarKumas}${it.astarFabricCode ? ` (${it.astarFabricCode})` : ""}`,
  ].filter(Boolean).join("   ·   ");
  doc.text(dp || "—", LX + 3, y + 6, { maxWidth: CW - 6 });
  y += 9;

  // ── Şema alanı ─────────────────────────────────────────────────────────────
  const TOP_PAD = pile > 0 ? 15 : 7;   // pile göstergesi için üst boşluk
  const BTM_PAD = isIki ? 22 : 15;      // alt ölçü çizgisi
  const LFT_PAD = 24;                   // boy ölçüsü
  const RGT_PAD = pile > 0 ? 18 : 5;   // pile annotation sağda

  const cX = LX + LFT_PAD;
  const cY = y + TOP_PAD;
  const cW = CW - LFT_PAD - RGT_PAD;   // ≈140mm
  const cH = 44;                        // sabit perde yüksekliği

  // Pile göstergesi (üstte kesik çizgili ham en)
  if (pile > 0) {
    const hamEn = (pile * wCm).toFixed(0);
    const pileY = cY - 8;
    doc.setDrawColor(160, 160, 160); doc.setLineWidth(0.2);
    try { doc.setLineDashPattern([2, 1.5], 0); } catch (_) {}
    doc.line(cX - 4, pileY, cX + cW + 4, pileY);
    try { doc.setLineDashPattern([], 0); } catch (_) {}
    // Uzatma çizgileri
    doc.setLineWidth(0.12);
    doc.line(cX - 4, pileY, cX - 4, cY);
    doc.line(cX + cW + 4, pileY, cX + cW + 4, cY);
    // Ham en ölçü çizgisi
    _dimLine(doc, cX - 4, pileY - 5, cX + cW + 4, pileY - 5, `Ham Kumaş: ${hamEn} cm`);
  }

  // Perde dikdörtgeni
  doc.setFillColor(240, 244, 252);
  doc.rect(cX, cY, cW, cH, "F");
  doc.setDrawColor(10, 10, 10); doc.setLineWidth(0.6);
  doc.rect(cX, cY, cW, cH);

  if (isIki) {
    // Çift kanat: ortadan kesik çizgi
    const midX = cX + cW / 2;
    doc.setDrawColor(70, 70, 70); doc.setLineWidth(0.28);
    try { doc.setLineDashPattern([2.8, 1.6], 0); } catch (_) {}
    doc.line(midX, cY, midX, cY + cH);
    try { doc.setLineDashPattern([], 0); } catch (_) {}

    // Her panelde kanat genişliği
    doc.setFont("NotoSans", "bold"); doc.setFontSize(11); doc.setTextColor(10, 10, 10);
    const wL = `${wingW % 1 === 0 ? wingW : wingW.toFixed(1)} cm`;
    doc.text(wL, cX + cW / 4, cY + cH / 2 - 1, { align: "center" });
    doc.text(wL, midX + cW / 4, cY + cH / 2 - 1, { align: "center" });

    // Her panelde ←→ ok
    const arY = cY + cH / 2 + 9;
    const ARW = 3;
    doc.setDrawColor(110, 110, 110); doc.setLineWidth(0.2);
    [[cX + 5, midX - 5], [midX + 5, cX + cW - 5]].forEach(([L, R]) => {
      doc.line(L, arY, R, arY);
      doc.line(L, arY - 1.1, L + ARW, arY); doc.line(L, arY + 1.1, L + ARW, arY);
      doc.line(R - ARW, arY - 1.1, R, arY); doc.line(R - ARW, arY + 1.1, R, arY);
    });
  } else {
    doc.setFont("NotoSans", "bold"); doc.setFontSize(12); doc.setTextColor(10, 10, 10);
    doc.text(`${wCm} cm`, cX + cW / 2, cY + cH / 2 + 2, { align: "center" });
  }

  // Köşe etiketleri
  doc.setFont("NotoSans", "bold"); doc.setFontSize(6.5); doc.setTextColor(140, 140, 140);
  doc.text(isIki ? "ÇİFT KANAT" : "TEK KANAT", cX + cW - 2, cY + 4, { align: "right" });
  doc.text(`${qty} adet`, cX + 2, cY + 4);

  // ── Ölçü oklarıyla mühendislik gösterimi ─────────────────────────────────
  const EG = 1.5, DO = 8;

  // Bitmiş En — alt
  const dimYW = cY + cH + DO;
  doc.setDrawColor(60, 60, 60); doc.setLineWidth(0.15);
  doc.line(cX,      cY + cH + EG, cX,      dimYW - EG);
  doc.line(cX + cW, cY + cH + EG, cX + cW, dimYW - EG);
  _dimLine(doc, cX, dimYW, cX + cW, dimYW, `Bitmiş En: ${wCm} cm`);

  // Çift kanatta kanat alt ölçüleri
  if (isIki) {
    const subY = dimYW + 7.5;
    const midX = cX + cW / 2;
    doc.setLineWidth(0.15);
    doc.line(midX, cY + cH + EG, midX, subY - EG);
    const wL2 = `${wingW % 1 === 0 ? wingW : wingW.toFixed(1)} cm`;
    _dimLine(doc, cX,   subY, midX,    subY, wL2);
    _dimLine(doc, midX, subY, cX + cW, subY, wL2);
  }

  // Bitmiş Boy — sol
  const dimXH = cX - DO;
  doc.setLineWidth(0.15);
  doc.line(cX - EG, cY,      dimXH + EG, cY);
  doc.line(cX - EG, cY + cH, dimXH + EG, cY + cH);
  _dimLine(doc, dimXH, cY, dimXH, cY + cH, `Bitmiş Boy: ${hCm} cm`);

  // Pile oranı — sağ kenar
  if (pile > 0) {
    doc.setFont("NotoSans", "bold"); doc.setFontSize(8);
    doc.setTextColor(30, 70, 180);
    doc.text(`Pile ×${pile}`, cX + cW + 12, cY + cH / 2, { angle: 90, align: "center" });
  }

  // Alt ince çizgi (kart ayırıcı)
  const cardEndY = y + TOP_PAD + cH + BTM_PAD + 3;
  doc.setDrawColor(210, 210, 210); doc.setLineWidth(0.2);
  doc.line(LX, cardEndY, LX + CW, cardEndY);

  return cardEndY + 4;
}

export async function buildWorkOrderDoc(order) {
  const doc  = new jsPDF({ unit: "mm", format: "a4" });
  await applyUnicodeFont(doc);
  // Görüntüleyicide okunur dosya adı — blob GUID'i yerine gerçek ad görünür
  doc.setProperties({ title: `ZEN-ImalatFisi-${ono(order)}.pdf`, subject: "İmalat Fişi (fiyatsız üretim belgesi)", author: "ZEN ERP" });
  const logoUrl = await loadLogoDataUrl();

  if (logoUrl) doc.addImage(logoUrl, "PNG", 14, 8, 36, 16);
  doc.setFont("NotoSans", "bold"); doc.setFontSize(13); doc.setTextColor(0);
  doc.text(bi("PRODUCTION SHEET", "İMALAT FİŞİ"), 105, 18, { align: "center" });
  doc.setFont("NotoSans", "normal"); doc.setFontSize(9); doc.setTextColor(50, 50, 50);
  doc.text(`${bi("Order No", "Sipariş No")}: ${ono(order)}`, 14, 32);
  doc.text(`${bi("Customer", "Müşteri")}: ${order.customerName || "—"}`, 14, 38);
  if (order.deadline) doc.text(`${bi("Due Date", "Termin")}: ${order.deadline}`, 130, 32);
  doc.text(`${bi("Date", "Tarih")}: ${new Date(order.createdAt || Date.now()).toLocaleDateString(MONEY.lang === "en" ? "en-GB" : "tr-TR")}`, 130, 38);
  doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.3);
  doc.line(14, 43, 196, 43);

  // ── Üretim kalemleri ─────────────────────────────────────────────────────
  // Tüm üretim kalemleri listelenir — ölçüsüz kalemler de fişte yer alır
  // (En/Boy sütununda "—" gösterilir). Siparişin kalemlerini listelemek
  // imalat fişinin temel amacıdır; ölçü yokluğu kalemi gizlememeli.
  const prodItems = (order.items || []).filter(it =>
    !it.isAccessory &&
    it.groupSlug !== "zip_aksesuar"
  );

  const buildRustikDetail = (it) => {
    const tipMap = { kanalli: "Alüminyum Kanallı", ahsap: "Ahşap Rustik", antik: "Antik & Retro" };
    const parts  = [tipMap[it.rustikTipi] || it.rustikTipi || "Rustik"];
    if (it.rustikTipi === "kanalli" && it.kanallıBaşlıkCesidi) parts.push(`Başlık: ${it.kanallıBaşlıkCesidi}`);
    if (it.rustikTipi === "ahsap"   && it.ahsapRenk)           parts.push(`Renk: ${it.ahsapRenk}`);
    if (it.rustikTipi === "antik"   && it.antikBoruCapi)        parts.push(`Çap: ${it.antikBoruCapi}`);
    if (it.rayOlsunMu) parts.push("Ray Dahil");
    return parts.join(" · ");
  };

  let y = 50;
  let pendingOther = []; // Tül/Fon olmayan birikmiş kalemler

  const flushOtherTable = async () => {
    if (pendingOther.length === 0) return;
    autoTable(doc, {
      startY: y,
      head: [["#", bi("Product", "Ürün"), bi("Technical Details", "Teknik Detaylar"), bi("En", "En"), bi("Boy", "Boy"), bi("Adet", "Adet")]],
      body: pendingOther,
      headStyles: { fillColor: [30, 30, 30], textColor: 255, fontStyle: "bold", font: "NotoSans", fontSize: 8 },
      styles:      { fontSize: 8, cellPadding: 2.5, font: "NotoSans" },
      columnStyles: {
        0: { cellWidth: 8 },
        2: { cellWidth: 68 },
        3: { halign: "center", cellWidth: 18 },
        4: { halign: "center", cellWidth: 18 },
        5: { halign: "center", cellWidth: 14 },
      },
    });
    y = doc.lastAutoTable.finalY + 4;
    pendingOther = [];
  };

  for (let i = 0; i < prodItems.length; i++) {
    const it       = prodItems[i];
    const isTulFon = it.groupSlug === "tul_fon_perde";
    const isPlise  = ["plise14", "plise20", "natty_plise", "plise25"].includes(it.groupSlug);

    if (isTulFon) {
      // Önce birikmiş diğer kalemleri tablo olarak çiz
      await flushOtherTable();

      // Sayfa yetmiyorsa yeni sayfa
      if (y + 85 > 272) {
        doc.addPage();
        await applyUnicodeFont(doc);
        y = 15;
      }

      y = _drawTulFonCard(doc, it, i + 1, y, 182);

    } else {
      // Tüm doldurulmuş alanları "Etiket: Değer" formatında göster
      const kumasStr = buildItemAllFields(it).join("\n") || "—";
      const hV = it.groupSlug === "rustik" ? "—" : numDash(it.heightCm);
      pendingOther.push([i + 1, pdfProduct(it.product), kumasStr, numDash(it.widthCm), hV, numDash(it.qty)]);
    }
  }

  // Kalan diğer kalemleri çiz
  await flushOtherTable();

  // ── Aksesuar / Ek Ürünler ──────────────────────────────────────────────────
  const accItems = (order.items || []).filter(it => it.isAccessory || it.groupSlug === "zip_aksesuar");
  if (accItems.length > 0) {
    if (y + 30 > 272) { doc.addPage(); await applyUnicodeFont(doc); y = 15; }
    doc.setFontSize(11); doc.setTextColor(0); doc.setFont("NotoSans", "bold");
    doc.text(bi("Accessories / Add-on Products", "Aksesuar / Ek Ürünler"), 14, y + 2);
    doc.setFont("NotoSans", "normal");
    autoTable(doc, {
      startY: y + 7,
      head: [["#", bi("Product", "Ürün"), bi("Qty", "Adet"), bi("Note", "Not")]],
      body: accItems.map((it, i) => [
        i + 1, pdfProduct(it.product || ""),
        it.qty || 1,
        [it.roomName && `${bi("Room", "Oda")}: ${it.roomName}`, it.note].filter(Boolean).join(" / "),
      ]),
      headStyles: { fillColor: [229, 50, 42], textColor: 255, fontStyle: "bold", font: "NotoSans" },
      styles: { fontSize: 9, cellPadding: 3, font: "NotoSans" },
      columnStyles: { 0: { cellWidth: 8 }, 2: { cellWidth: 16, halign: "center" } },
    });
    y = doc.lastAutoTable.finalY;
  }

  // ── Notlar ────────────────────────────────────────────────────────────────
  const yEnd = y + 12;
  doc.setFontSize(9); doc.setTextColor(80);
  doc.text(bi(
    "Cutting note: sizes are NET. Mechanism / cassette allowance added by craftsman.",
    "Kesim Notu: Verilen ölçüler NET ölçülerdir. Mekanizma / kasa payı ustada eklenmelidir."
  ), 14, yEnd, { maxWidth: 182 });
  if (order.notes) {
    doc.setFont("NotoSans", "bold");
    doc.text(bi("Special Notes", "Özel Notlar") + ":", 14, yEnd + 8);
    doc.setFont("NotoSans", "normal");
    doc.text(order.notes, 14, yEnd + 14, { maxWidth: 180 });
  }
  doc.setFontSize(8); doc.setTextColor(120);
  doc.text(
    bi("This document contains no prices - for production use only.", "Bu belge fiyat içermez - sadece üretim için kullanılır.") +
    "  ZEN Mekanizmalı Perde Sistemleri",
    14, 285
  );
  return doc;
}

export async function generateWorkOrderPDF(order) {
  const doc = await buildWorkOrderDoc(order);
  doc.save(`ZEN-ImalatFisi-${ono(order)}.pdf`);
  toast.success("İmalat Fişi indirildi");
}

export async function buildWholesalerDoc(order) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  await applyUnicodeFont(doc);
  // Görüntüleyicide okunur dosya adı — blob GUID'i yerine gerçek ad görünür
  doc.setProperties({ title: `ZEN-Toptanci-${ono(order)}.pdf`, subject: "Toptancı Sipariş Fişi", author: "ZEN ERP" });
  const logoUrl = await loadLogoDataUrl();
  if (logoUrl) doc.addImage(logoUrl, "PNG", 14, 12, 40, 18);
  doc.setFont("NotoSans", "bold"); doc.setFontSize(18); doc.setTextColor(0);
  doc.text(bi("WHOLESALER ORDER SHEET", "TOPTANCI SİPARİŞ FİŞİ"), 105, 22, { align: "center" });
  doc.setFont("NotoSans", "normal"); doc.setFontSize(9);
  doc.text(`${bi("Order", "Sipariş")}: ${ono(order)}  ·  ${bi("Customer", "Müşteri")}: ${dash(order.customerName)}`, 14, 40);
  doc.text(`${bi("Due Date", "Termin")}: ${order.deadline || "-"}  ·  ${bi("Date", "Tarih")}: ${new Date().toLocaleDateString(MONEY.lang === "en" ? "en-GB" : "tr-TR")}`, 14, 46);
  autoTable(doc, {
    startY: 54,
    head: [["#", bi("Product / System", "Ürün / Sistem"), bi("Fabric / RAL", "Kumaş / RAL"), bi("Width", "En"), bi("Height", "Boy"), bi("Qty", "Adet"), bi("Specifications", "Özellikler")]],
    body: (order.items || []).map((it, i) => {
      const ozellikler = [...fullItemDesc(it), it.midFoot && `Montaj Ayağı: ${it.midFoot}`, it.cableDir && `Kablo/Yön: ${it.cableDir}`, it.extrasLabel && `Ekstra: ${it.extrasLabel}`, it.slatColorCode && `Slat Rengi: ${it.slatColorCode}`, it.note].filter(Boolean).join(" · ");
      return [i + 1, pdfProduct(it.product), (it.fabric || "-") + (it.ralCode ? ` / RAL ${it.ralCode}` : ""), cmText(it.widthCm), cmText(it.heightCm), numDash(it.qty), ozellikler || "-"];
    }),
    headStyles: { fillColor: [30, 30, 30], textColor: 255, fontStyle: "bold", font: "NotoSans" },
    styles: { fontSize: 8, cellPadding: 2.5, font: "NotoSans" },
    columnStyles: { 6: { cellWidth: 55 } },
  });
  const y = doc.lastAutoTable.finalY + 12;
  doc.setFontSize(8); doc.setTextColor(80);
  doc.text(bi("Please check the sizes before production. For special colour / RAL codes we await your approval.", "Lütfen ölçüleri kontrol ederek üretime alın. Özel renk / RAL kodu ise onay bekliyoruz."), 14, y);
  if (order.notes) {
    doc.setFont("NotoSans", "bold"); doc.text(bi("Note", "Not") + ":", 14, y + 6); doc.setFont("NotoSans", "normal");
    doc.text(order.notes, 22, y + 6, { maxWidth: 180 });
  }
  doc.setFontSize(8); doc.setTextColor(120);
  doc.text("ZEN Mekanizmalı Perde Sistemleri  ·  info@zenperde.com  ·  www.zenperde.com", 14, 285);
  return doc;
}

export async function generateWholesalerPDF(order) {
  const doc = await buildWholesalerDoc(order);
  doc.save(`ZEN-Toptanci-${ono(order)}.pdf`);
  toast.success("Toptancı Fişi indirildi");
}

export async function buildLabelDoc(order, opts = { w: 70, h: 40 }) {
  const doc = new jsPDF({ unit: "mm", format: [opts.w, opts.h], orientation: opts.w > opts.h ? "landscape" : "portrait" });
  await applyUnicodeFont(doc);
  // Görüntüleyicide okunur dosya adı — blob GUID'i yerine gerçek ad görünür
  doc.setProperties({ title: `ZEN-Etiket-${ono(order)}.pdf`, subject: `Etiket (${opts.w}×${opts.h} mm)`, author: "ZEN ERP" });
  const items = order.items || [];
  const makeBarcode = (text) => {
    try {
      const canvas = document.createElement("canvas");
      JsBarcode(canvas, text, { format: "CODE128", displayValue: false, height: 40, margin: 0 });
      return canvas.toDataURL("image/png");
    } catch (e) { return null; }
  };
  items.forEach((it, idx) => {
    if (idx > 0) doc.addPage([opts.w, opts.h], opts.w > opts.h ? "landscape" : "portrait");
    doc.setFontSize(8); doc.setFont("NotoSans", "bold"); doc.setTextColor(229, 50, 42);
    doc.text("ZEN", 3, 5);
    doc.setFontSize(6); doc.setTextColor(0); doc.setFont("NotoSans", "normal");
    doc.text(ono(order), opts.w - 3, 5, { align: "right" });
    doc.setFontSize(7); doc.setFont("NotoSans", "bold");
    doc.text((order.customerName || "").substring(0, 28), 3, 10);
    doc.setFontSize(6); doc.setFont("NotoSans", "normal");
    if (order.shippingAddress) doc.text(order.shippingAddress.substring(0, 40), 3, 14, { maxWidth: opts.w - 6 });
    doc.setFontSize(8); doc.setFont("NotoSans", "bold");
    // Ölçüsüz kalemlerde "undefined x undefined cm" yerine "—" basılır
    const olcuStr = (Number(it.widthCm) > 0 && Number(it.heightCm) > 0)
      ? `${it.widthCm} x ${it.heightCm} cm`
      : "—";
    doc.text(olcuStr, 3, opts.h - 15);
    doc.setFontSize(6); doc.setFont("NotoSans", "normal");
    doc.text(`${(it.product || "").substring(0, 30)}`, 3, opts.h - 11);
    doc.text(`Adet: ${numDash(it.qty)}  ·  #${idx + 1}/${items.length}`, 3, opts.h - 7);
    const bc = makeBarcode(`${ono(order)}-${idx + 1}`);
    if (bc) {
      const bcW = Math.min(opts.w - 6, 30); const bcH = 4;
      doc.addImage(bc, "PNG", 3, opts.h - 5, bcW, bcH);
    }
  });
  return doc;
}

export async function generateLabelPDF(order, opts = { w: 70, h: 40, autoprint: false }) {
  const doc = await buildLabelDoc(order, opts);
  const items = order.items || [];
  if (opts.autoprint) {
    doc.autoPrint();
    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);
    printViaIframe(url, true, url);
  } else { doc.save(`ZEN-Etiket-${ono(order)}.pdf`); }
  toast.success(`${items.length} etiket ${opts.autoprint ? "yazıcıya" : "PDF olarak"} gönderildi (${opts.w}x${opts.h}mm)`);
}

export async function generateQuotePDFDoc(order) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  await applyUnicodeFont(doc);
  const logoUrl = await loadLogoDataUrl();
  if (logoUrl) doc.addImage(logoUrl, "PNG", 14, 12, 40, 18);
  const cur = order.currency || "TL";
  doc.setFontSize(18); doc.setTextColor(229, 50, 42);
  doc.text(bi("QUOTE / ORDER", "TEKLİF / SİPARİŞ"), 196, 22, { align: "right" });
  doc.setFontSize(9.5); doc.setTextColor(60);
  doc.text(`${bi("Document No", "Belge No")}: ${ono(order)}`, 14, 40);
  doc.text(`${bi("Customer", "Müşteri")}: ${order.customerName || "-"}`, 14, 46);
  if (order.projectName) doc.text(`${bi("Project", "Proje")}: ${order.projectName}`, 14, 52);
  doc.text(`${bi("Date", "Tarih")}: ${new Date(order.createdAt || Date.now()).toLocaleDateString(MONEY.lang === "en" ? "en-GB" : "tr-TR")}`, 150, 40);
  if (order.deadline) doc.text(`${bi("Due Date", "Termin")}: ${order.deadline}`, 150, 46);
  autoTable(doc, {
    startY: 58,
    head: [["#", bi("Product", "Ürün"), bi("Fabric", "Kumaş"), bi("Width (cm)", "Genişlik (cm)"), bi("Height (cm)", "Yükseklik (cm)"), bi("Qty", "Adet"), bi("Unit Price", "B.Fiyat"), bi("VAT", "KDV"), bi("Amount", "Tutar")]],
    body: expandQuoteRows(order.items).map((it, i) => [
      i + 1,
      pdfProduct(it.product) + (quoteItemDesc(it) ? `\n${quoteItemDesc(it)}` : ""),
      it.fabric || (it.slatSize ? `Slat ${it.slatSize}mm${it.slatColorCode ? ` / ${it.slatColorCode}` : ""}` : "-"),
      it.widthCm || "-", it.heightCm || "-", it.qty || 1,
      pdfMoney((Number(it.unitPrice) > 0 ? it.unitPrice : (it.lineTotal / Math.max(1, it.qty || 1))), it.currency || cur), `%${it.kdvRate ?? 20}`, pdfMoney(it.lineTotal, it.currency || cur),
    ]),
    headStyles: { fillColor: [229, 50, 42], textColor: 255, fontStyle: "bold", fontSize: 8.5, font: "NotoSans" },
    styles: { fontSize: 8.5, cellPadding: 3, font: "NotoSans" },
    columnStyles: { 0: { cellWidth: 8 }, 3: { halign: "center" }, 4: { halign: "center" }, 5: { halign: "center" }, 6: { halign: "right" }, 8: { halign: "right", fontStyle: "bold" } },
  });
  let y = doc.lastAutoTable.finalY + 8;
  doc.setFontSize(10); doc.setTextColor(30);
  doc.text(`${bi("Subtotal", "Ara Toplam")}: ${pdfMoney(order.subtotal, cur)}`, 196, y, { align: "right" });
  y += 6;
  if (order.discountPercent) { doc.text(`${bi("Discount", "İndirim")} (%${order.discountPercent}): -${pdfMoney((order.subtotal * order.discountPercent) / 100, cur)}`, 196, y, { align: "right" }); y += 6; }
  for (const g of (order.vatBreakdown || []).slice().sort((a, b) => a.rate - b.rate)) { doc.text(`${bi("VAT", "KDV")} %${g.rate}: ${pdfMoney(g.kdv, cur)}`, 196, y, { align: "right" }); y += 6; }
  doc.setFontSize(13); doc.setFont("NotoSans", "bold"); doc.setTextColor(229, 50, 42);
  doc.text(`${bi("GRAND TOTAL", "GENEL TOPLAM")}: ${pdfMoney(order.total, cur)}`, 196, y + 3, { align: "right" });
  doc.setFont("NotoSans", "normal");
  const rates = await fetchRates();
  const vatTotal = (order.vatBreakdown || []).reduce((s, g) => s + (g.kdv || 0), 0) || order.vatAmount || 0;
  const totalConv = convAllCurrencies(order.total, cur, rates);
  const netConv = convAllCurrencies((order.total || 0) - vatTotal, cur, rates);
  const CONV_KEY = { TL: "tl", EUR: "eur", USD: "usd" };
  const otherCurs = ["TL", "EUR", "USD"].filter(c => c !== cur);
  let y2 = y + 11;
  doc.setFontSize(8.5); doc.setTextColor(90);
  doc.text(bi("Other Amounts (excl. VAT / incl. VAT)", "Diğer Tutarlar (KDV Hariç / KDV Dahil)") + ":", 196, y2, { align: "right" });
  y2 += 5;
  for (const c of otherCurs) { doc.text(`${c}: ${pdfMoney(netConv[CONV_KEY[c]], c)} / ${pdfMoney(totalConv[CONV_KEY[c]], c)}`, 196, y2, { align: "right" }); y2 += 5; }
  if (order.notes) { doc.setFontSize(9); doc.setTextColor(80); doc.text(`${bi("Notes", "Notlar")}: ${order.notes}`, 14, y2 + 6, { maxWidth: 180 }); }
  doc.setFontSize(8); doc.setTextColor(120);
  doc.text(bi("Quote valid for 7 days", "Teklif 7 gün geçerlidir") + " - ZEN Mekanizmalı Perde Sistemleri - www.zenperde.com - info@zenperde.com", 105, 287, { align: "center" });
  return { doc, filename: `ZEN-Teklif-${ono(order)}.pdf` };
}

export function buildOrderSummaryText(order) {
  const cur = order.currency || "TL";
  const sym = CURRENCY_SYMBOL[cur] || cur;
  const lines = expandQuoteRows(order.items).map((it, i) => {
    const olcu = it.widthCm && it.heightCm ? ` ${it.widthCm}×${it.heightCm}cm` : "";
    const desc = quoteItemDesc(it);
    return `${i + 1}. ${it.product || "-"}${olcu} × ${it.qty || 1} ad → ${sym}${Number(it.lineTotal || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}${desc ? `\n   _${desc}_` : ""}`;
  });
  return [
    `*ZEN Perde — Teklif / Sipariş*`,
    `Belge No: ${ono(order)}`,
    `Müşteri: ${order.customerName || "-"}`,
    order.projectName ? `Proje: ${order.projectName}` : null,
    `Tarih: ${new Date(order.createdAt || Date.now()).toLocaleDateString("tr-TR")}`,
    ``,
    ...lines, ``,
    `Ara Toplam: ${sym}${Number(order.subtotal || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`,
    order.discountPercent ? `İndirim: %${order.discountPercent}` : null,
    `*GENEL TOPLAM: ${sym}${Number(order.total || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}*`,
    ``, `Teşekkür ederiz — ZEN Mekanizmalı Perde Sistemleri`,
  ].filter(l => l !== null).join("\n");
}

async function uploadPdfForSharing(blob, filename, ref = null) {
  const fd = new FormData();
  fd.append("file", blob, filename);
  if (ref?.refType) fd.append("refType", ref.refType);
  if (ref?.refId) fd.append("refId", ref.refId);
  const { data } = await api.post("/share/pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return shareUrl(data.path);
}

export async function smartShareFile(doc, filename, via, text, phone = "", ref = null) {
  const blob = doc.output("blob");
  const file = new File([blob], filename, { type: "application/pdf" });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try { await navigator.share({ files: [file], title: filename, text }); return; }
    catch (e) { if (e.name === "AbortError") return; }
  }
  let link = "";
  try {
    toast.loading("PDF hazırlanıyor…", { id: "share-pdf" });
    link = await uploadPdfForSharing(blob, filename, ref);
    toast.dismiss("share-pdf");
  } catch (e) {
    toast.dismiss("share-pdf");
    doc.save(filename);
    toast.error("PDF linki oluşturulamadı — dosya bilgisayarınıza indirildi");
    if (via === "email") window.location.href = `mailto:?subject=${encodeURIComponent(filename.replace(".pdf", ""))}&body=${encodeURIComponent(text)}`;
    else whatsappShare(phone, text);
    return;
  }
  const body = `${text}\n\nPDF: ${link}`;
  if (via === "email") {
    window.location.href = `mailto:?subject=${encodeURIComponent(filename.replace(".pdf", ""))}&body=${encodeURIComponent(body)}`;
    toast.success("E-posta taslağı açıldı — PDF indirme linki mesaja eklendi");
  } else {
    whatsappShare(phone, body);
    toast.success("WhatsApp açıldı — mesaja PDF indirme linki eklendi");
  }
}

export async function smartSharePDF(order, via, phone = "") {
  const { doc, filename } = await generateQuotePDFDoc(order);
  await smartShareFile(doc, filename, via, buildOrderSummaryText(order), phone, { refType: "order", refId: order.id });
}

import { FileText, Factory, Receipt, Printer as PrinterIcon, Scissors } from "lucide-react";
export const PREVIEW_DOC_TYPES = [
  { key: "teklif", label: "Teklif / Fatura", en: "Quote / Invoice", icon: FileText },
  { key: "imalat", label: "İmalat Fişi", en: "Production Sheet", icon: Factory },
  { key: "tulfon", label: "Terzi Föyü · Tül / Fon", en: "Curtain Tailor Sheet", icon: Scissors },
  { key: "toptanci", label: "Toptancı Sipariş", en: "Wholesaler Order", icon: Receipt },
  { key: "etiket", label: "Etiket (70×40)", en: "Label (70×40)", icon: PrinterIcon },
];

// ── Etiket Baskı Yardımcıları ─────────────────────────────────────────────────

// Firma logosunu ZPL ^GF bitmap komutuna dönüştürür (Canvas tabanlı, async)
// widthMm/heightMm: etiket üzerindeki boyut (mm), MM_TO_DOT = 8 dot/mm
export async function logoToZPLGF(logoDataUrl, widthMm = 30, heightMm = 12) {
  if (!logoDataUrl) return null;
  const MM_TO_DOT = 8;
  const widthDots = Math.round(widthMm * MM_TO_DOT);
  const heightDots = Math.round(heightMm * MM_TO_DOT);
  try {
    const canvas = document.createElement("canvas");
    canvas.width = widthDots;
    canvas.height = heightDots;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, widthDots, heightDots);
    const img = new Image();
    img.crossOrigin = "anonymous";
    await new Promise((res) => { img.onload = res; img.onerror = res; img.src = logoDataUrl; });
    ctx.drawImage(img, 0, 0, widthDots, heightDots);
    const { data: px } = ctx.getImageData(0, 0, widthDots, heightDots);
    const bytesPerRow = Math.ceil(widthDots / 8);
    let hex = "";
    for (let row = 0; row < heightDots; row++) {
      for (let bIdx = 0; bIdx < bytesPerRow; bIdx++) {
        let byte = 0;
        for (let bit = 0; bit < 8; bit++) {
          const x = bIdx * 8 + bit;
          if (x < widthDots) {
            const i = (row * widthDots + x) * 4;
            const gray = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
            if (gray < 128) byte |= (0x80 >> bit);
          }
        }
        hex += byte.toString(16).padStart(2, "0").toUpperCase();
      }
    }
    const totalBytes = bytesPerRow * heightDots;
    return `^GFB,${totalBytes},${bytesPerRow},${widthDots},${hex}`;
  } catch (e) {
    console.warn("[ZPL Logo] ^GF dönüşüm hatası:", e);
    return null;
  }
}

// HTML etiket şablonundan HTML string üretir (from html_label_templates)
function _buildHtmlFromStoredTemplate(order, tpl, logoDataUrl = "") {
  const SIZES = { kargo: { w: 100, h: 75 }, kasa: { w: 100, h: 50 } };
  const preset = tpl.size === "custom"
    ? { w: tpl.customW || 100, h: tpl.customH || 60 }
    : (SIZES[tpl.size] || { w: 100, h: 60 });
  const { w, h } = preset;

  const RESOLVERS = {
    static_text:          (o, v) => v || "",
    field_customer_name:  (o) => (o?.customerName || "").slice(0, 40),
    field_customer_city:  (o) => (o?.customerCity || o?.city || "").slice(0, 24),
    field_project_name:   (o) => (o?.projectName || "").slice(0, 40),
    field_order_no:       (o) => (o?.orderNo || o?.id || "SP-000").toString().slice(0, 18),
    field_product_name:   (o) => (o?.items?.[0]?.product || o?.productType || "").slice(0, 40),
    field_dimensions:     (o) => { const i = o?.items?.[0]; return i ? `${i.widthCm||150}×${i.heightCm||200} cm` : ""; },
    field_product_width:  (o) => o?.items?.[0]?.widthCm ? `${o.items[0].widthCm} cm` : "",
    field_product_height: (o) => o?.items?.[0]?.heightCm ? `${o.items[0].heightCm} cm` : "",
    field_m2:             (o) => { const i = o?.items?.[0]; return `${(((i?.widthCm||150)*(i?.heightCm||200))/10000).toFixed(2)} m²`; },
    field_quantity:       (o) => `${o?.items?.[0]?.qty || o?.totalQty || 1} ad`,
    field_fabric_code:    (o) => (o?.fabricKodu || o?.items?.[0]?.fabricCode || "").slice(0, 30),
    field_ral_code:       (o) => (o?.ralKodu || o?.items?.[0]?.ralCode || "").slice(0, 20),
    field_date:           () => new Date().toLocaleDateString("tr-TR"),
    field_deadline:       (o) => o?.deadline ? new Date(o.deadline).toLocaleDateString("tr-TR") : "",
    field_note:           (o) => (o?.note || o?.deliveryNote || "").slice(0, 40),
    barcode:              (o) => (o?.orderNo || o?.id || "ZEN000000").toString().slice(0, 20),
  };

  const els = (tpl.elements || []).map((el) => {
    const resolve = RESOLVERS[el.type];
    const value = resolve ? resolve(order, el.text || "") : "";
    const x = (el.x_mm || 0).toFixed(2);
    const y = (el.y_mm || 0).toFixed(2);
    const ptSize = ((el.fontSize || 26) * 0.354 * 0.125 * 3.78).toFixed(1);

    if (el.type === "rect") {
      return `<div style="position:absolute;left:${x}mm;top:${y}mm;width:${(el.widthMm||40).toFixed(2)}mm;height:${(el.heightMm||20).toFixed(2)}mm;border:${(el.borderThick||0.5).toFixed(2)}mm solid #000;box-sizing:border-box;"></div>`;
    }
    if (el.type === "image_logo") {
      const iw = (el.widthMm || 30).toFixed(2);
      const ih = (el.heightMm || 12).toFixed(2);
      return logoDataUrl
        ? `<div style="position:absolute;left:${x}mm;top:${y}mm;width:${iw}mm;height:${ih}mm;"><img src="${logoDataUrl}" style="width:100%;height:100%;object-fit:contain;" /></div>`
        : "";
    }
    if (el.type === "hline") {
      return `<div style="position:absolute;left:${x}mm;top:${y}mm;width:${(el.widthMm||w-4).toFixed(2)}mm;height:${Math.max(0.3,(el.fontSize||2)*0.03).toFixed(2)}mm;background:#000;"></div>`;
    }
    if (el.type === "barcode") {
      return `<div style="position:absolute;left:${x}mm;top:${y}mm;width:${(el.widthMm||60).toFixed(2)}mm;height:${(el.heightMm||18).toFixed(2)}mm;border:0.2mm solid #ccc;display:flex;align-items:center;justify-content:center;font-family:monospace;font-size:5pt;">▐▌▌▐▌▐▌▐▌▐▌▐▐▌ ${String(value||"").replace(/</g,"&lt;")}</div>`;
    }
    const rot = el.rotation ? "transform:rotate(90deg);transform-origin:top left;" : "";
    return `<div style="position:absolute;left:${x}mm;top:${y}mm;font-size:${ptSize}pt;font-weight:700;font-family:Arial,sans-serif;white-space:nowrap;${rot}">${String(value||"").replace(/</g,"&lt;")}</div>`;
  }).join("\n");

  return `<!DOCTYPE html><html lang="tr"><head><meta charset="utf-8"><meta name="color-scheme" content="light">
<style>@page{size:${w}mm ${h}mm;margin:0;}*{box-sizing:border-box;}html,body{width:${w}mm;height:${h}mm;background:#fff;margin:0;padding:0;overflow:hidden;}</style>
</head><body><div style="position:relative;width:${w}mm;height:${h}mm;">${els}</div></body></html>`;
}

// Sipariş için HTML kargo/kasa etiketini gizli iframe ile yazdırır — yeni sekme açılmaz
export async function printHtmlLabelForOrder(order, type = "kargo") {
  let tpl = null;
  try {
    const { data } = await api.get("/settings", { params: { category: "html_label_templates" } });
    const row = (data || []).find((r) => r.key === type);
    if (row?.value) tpl = JSON.parse(row.value);
  } catch { /* fallback */ }

  let logoDataUrl = null;
  if (tpl && (tpl.elements || []).some((el) => el.type === "image_logo")) {
    try { logoDataUrl = await loadLogoDataUrl(); } catch { /* no logo */ }
  }

  const html = tpl
    ? _buildHtmlFromStoredTemplate(order, tpl, logoDataUrl)
    : buildCargoLabelHTML(order);

  printViaIframe(html, false);
}



// ─── Tedarikçi Ekstre PDF ─────────────────────────────────────────────────────
export async function buildSupplierStatementPdf(supplier, rows) {
  const { jsPDF } = await import("jspdf");
  const { default: autoTable } = await import("jspdf-autotable");

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  await applyUnicodeFont(doc);

  const RED    = [229, 50,  42];
  const DARK   = [15,  23,  42];
  const GRAY   = [100, 116, 139];
  const GREEN  = [16,  185, 129];
  const W      = doc.internal.pageSize.getWidth();
  const today  = new Date().toLocaleDateString("tr-TR");
  const cur    = supplier.currency || "TL";

  const fmtMoney = (v) =>
    Math.abs(v).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ── Üst başlık ────────────────────────────────────────────────
  doc.setFillColor(...RED);
  doc.rect(0, 0, W, 28, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("NotoSans", "bold");
  doc.setFontSize(16);
  doc.text("TEDARİKÇİ EKSTRESİ", 14, 13);
  doc.setFontSize(9);
  doc.setFont("NotoSans", "normal");
  doc.text(`Oluşturma Tarihi: ${today}`, 14, 21);
  doc.text("ZEN PERDE ERP", W - 14, 13, { align: "right" });

  // ── Tedarikçi Bilgileri ───────────────────────────────────────
  doc.setTextColor(...DARK);
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(14, 32, W - 28, 28, 3, 3, "F");
  doc.setFont("NotoSans", "bold");
  doc.setFontSize(13);
  doc.text(supplier.name, 20, 42);
  doc.setFont("NotoSans", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  const infoLine = [supplier.phone, supplier.address, supplier.taxNo ? `VKN: ${supplier.taxNo}` : ""]
    .filter(Boolean).join("   ·   ");
  if (infoLine) doc.text(infoLine, 20, 50);
  if (supplier.note) doc.text(supplier.note, 20, 56);

  // Bakiye kutusu
  const balX = W - 80;
  doc.setFillColor(supplier.balance < 0 ? 255 : 240, supplier.balance < 0 ? 235 : 253, supplier.balance < 0 ? 235 : 244);
  doc.roundedRect(balX, 33, 62, 26, 3, 3, "F");
  doc.setFontSize(8);
  doc.setFont("NotoSans", "normal");
  doc.setTextColor(...GRAY);
  doc.text(supplier.balance < 0 ? "Borcum (Net)" : supplier.balance > 0 ? "Alacağım" : "Bakiye", balX + 4, 40);
  doc.setFontSize(16);
  doc.setFont("NotoSans", "bold");
  doc.setTextColor(...(supplier.balance < 0 ? RED : GREEN));
  doc.text(`${fmtMoney(supplier.balance)} ${cur}`, balX + 4, 53);

  // ── Tablo ─────────────────────────────────────────────────────
  const tableRows = rows.map((r) => [
    new Date(r.date).toLocaleDateString("tr-TR"),
    r.description + (r.note ? `\n${r.note}` : ""),
    r.borc  > 0 ? `${fmtMoney(r.borc)} ${cur}`  : "—",
    r.odeme > 0 ? `${fmtMoney(r.odeme)} ${cur}` : "—",
    `${fmtMoney(r.bakiye)} ${cur}`,
  ]);

  const totalBorc  = rows.reduce((s, r) => s + r.borc,  0);
  const totalOdeme = rows.reduce((s, r) => s + r.odeme, 0);

  autoTable(doc, {
    startY: 65,
    head: [["Tarih", "Açıklama", "Borç", "Ödeme", "Bakiye"]],
    body: tableRows,
    foot: [["", "TOPLAM", `${fmtMoney(totalBorc)} ${cur}`, `${fmtMoney(totalOdeme)} ${cur}`, `${fmtMoney(supplier.balance)} ${cur}`]],
    styles: { font: "NotoSans", fontSize: 8, cellPadding: 3, overflow: "linebreak" },
    headStyles: { fillColor: RED, textColor: 255, fontStyle: "bold", fontSize: 8 },
    footStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: "bold", fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 22 },
      1: { cellWidth: "auto" },
      2: { cellWidth: 32, halign: "right", textColor: [220, 38, 38] },
      3: { cellWidth: 32, halign: "right", textColor: [5, 150, 105] },
      4: { cellWidth: 35, halign: "right", fontStyle: "bold" },
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    didDrawPage: (data) => {
      const pg = doc.internal.getCurrentPageInfo().pageNumber;
      const total = doc.internal.getNumberOfPages();
      doc.setFont("NotoSans", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...GRAY);
      doc.text(`Sayfa ${pg} / ${total}`, W / 2, doc.internal.pageSize.getHeight() - 8, { align: "center" });
    },
  });

  doc.save(`Ekstre-${supplier.name || "tedarikci"}.pdf`);
}



// ── Tenteks Sipariş Föyü PDF ──────────────────────────────────────────────────
/**
 * Tenteks ürünler için iç sipariş föyü HTML'i üretir.
 * @param {object} order   Teklif/sipariş objesi
 * @param {object} opts    { isAdmin: boolean } — admin ise alış fiyatı gösterilir
 */
export function buildTenteksSiparisFoyuHTML(order, { isAdmin = false, fabricImages = {} } = {}) {
  const TENTEKS_SET = new Set(["kasetli_tente","mafsalli_tente","giyotin_cam","cam_tavan","surme_cam_sistemi","pergola","bioklimatik","pencere_tentesi"]);
  const items = (order.items || []).filter(it => TENTEKS_SET.has(it.groupSlug));
  if (!items.length) return null;

  const fmtEur = n => `€\u00A0${Number(n||0).toLocaleString("tr-TR",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
  const esc = s => String(s||"").replace(/</g,"&lt;").replace(/>/g,"&gt;");

  const detRow = (k, v, bold) => v ? `<tr>
    <td style="padding:4px 8px;font-weight:600;color:#64748B;width:44%;border:1px solid #E2E8F0;font-size:10px;white-space:nowrap">${esc(k)}</td>
    <td style="padding:4px 8px;${bold?"font-weight:700;":""}color:#1E293B;border:1px solid #E2E8F0;font-size:10.5px">${esc(v)}</td></tr>` : "";

  const getSection = (slug, w) => {
    const n = Number(w||0);
    if (!n || (slug!=="pergola" && slug!=="bioklimatik")) return null;
    if (slug==="bioklimatik") return n<=400
      ? {zone:"Sarı Bölge",bolme:1,ray:2,bg:"#FEFCE8",border:"#CA8A04",fg:"#713F12"}
      : {zone:"Mavi Bölge",bolme:2,ray:3,bg:"#EFF6FF",border:"#3B82F6",fg:"#1E3A8A"};
    if (n<=400) return {zone:"Sarı Bölge",bolme:1,ray:2,bg:"#FEFCE8",border:"#CA8A04",fg:"#713F12"};
    if (n<=900) return {zone:"Mavi Bölge",bolme:2,ray:3,bg:"#EFF6FF",border:"#3B82F6",fg:"#1E3A8A"};
    return {zone:"Pembe Bölge",bolme:3,ray:4,bg:"#FDF2F8",border:"#DB2777",fg:"#831843"};
  };

  const dimLabels = slug => {
    if (slug==="kasetli_tente") return ["Genişlik","Açılım"];
    if (slug==="pergola"||slug==="bioklimatik") return ["Cephe (En)","Açılım (Boy)"];
    return ["En","Boy"];
  };

  const accLines = it => {
    const r=[];
    if (it.tentMotor)                      r.push(["Motor", it.tentMotor]);
    if (it.tentControl)                    r.push(["Kumanda", it.tentControl]);
    if (it.tentSensor_ruzgarSensoru)       r.push(["Rüzgar Sensörü","Evet"]);
    if (it.tentSensor_yagmurSensoru)       r.push(["Yağmur Sensörü","Evet"]);
    if (it.tentLed)                        r.push(["LED Aydınlatma", it.tentLed]);
    if (it.tentSacakYazi)                  r.push(["Saçak Yazı","Evet"]);
    if (it.kolTipi)                        r.push(["Kol Tipi", it.kolTipi]);
    return r;
  };

  const insaiLines = it => {
    const r=[];
    if (it.tentInsaiAlinTip)     r.push(`Alın Profili: ${it.tentInsaiAlinTip}${it.tentInsaiAlinMiktar?` × ${it.tentInsaiAlinMiktar} m`:""}`);
    if (it.tentInsaiDikmeTip)    r.push(`Dikme: ${it.tentInsaiDikmeTip}${it.tentInsaiDikmeMiktar?` × ${it.tentInsaiDikmeMiktar} adet`:""}`);
    if (it.tentInsaiAraKayitTip) r.push(`Ara Kayıt: ${it.tentInsaiAraKayitTip}${it.tentInsaiAraKayitMiktar?` × ${it.tentInsaiAraKayitMiktar} m`:""}`);
    if (it.tentInsaiLedTip)      r.push(`LED Kontrol: ${it.tentInsaiLedTip}`);
    if (it.tentInsaiEkstraAskuTip) r.push(`Ekstra Askı: ${it.tentInsaiEkstraAskuTip}${it.tentInsaiEkstraAskuMiktar?` × ${it.tentInsaiEkstraAskuMiktar} adet`:""}`);
    return r;
  };

  const cards = items.map((it, idx) => {
    const [lbl1, lbl2] = dimLabels(it.groupSlug);
    const sec = getSection(it.groupSlug, it.widthCm);
    const accs = accLines(it);
    const ins = insaiLines(it);
    const qty = it.qty || 1;
    const satisBirim = Number(it.unitPrice||0);
    const alisUnitPrice = satisBirim / 1.20;
    const satisToplam  = satisBirim * qty;
    const alisToplam   = alisUnitPrice * qty;

    // Kumaş kodu: "Blanco (2042)" → "2042"
    const fabricCode = it.fabric?.match(/\((\d+)\)$/)?.[1] || null;
    const fabricImgSrc = fabricCode ? fabricImages[fabricCode] : null;

    const renkLines = [
      it.fabric          && ["Kumaş",        it.fabric],
      it.tentFabricColor && ["Kumaş Rengi",  it.tentFabricColor],
      it.tentRalCode     && ["Profil RAL",   it.tentRalCode],
      it.ralCode && !it.tentRalCode && ["RAL Kodu", it.ralCode],
    ].filter(Boolean);

    return `<div style="page-break-inside:avoid;border:1.5px solid #CBD5E1;border-radius:8px;padding:14px 16px;margin-bottom:14px;background:#FFFFFF">
  <!-- item header -->
  <div style="display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:10px;border-bottom:2px solid #E2E8F0;margin-bottom:10px">
    <div>
      <div style="font-size:8px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#94A3B8">KALEM ${idx+1}</div>
      <div style="font-size:14px;font-weight:800;color:#1E293B;margin-top:2px">${esc(it.product||"—")}</div>
      ${it.matrixVariant ? `<div style="font-size:10px;color:#64748B;margin-top:2px">Model: ${esc(it.matrixVariant)}</div>` : ""}
    </div>
    <div style="text-align:right">
      <div style="font-size:8px;color:#94A3B8;text-transform:uppercase;letter-spacing:1px">Adet</div>
      <div style="font-size:22px;font-weight:900;color:#E5322A;line-height:1">${qty}</div>
    </div>
  </div>
  <!-- two-col grid -->
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:10px">
    <div>
      <div style="font-size:8px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:#94A3B8;margin-bottom:5px">ÖLÇÜLER</div>
      <table style="width:100%;border-collapse:collapse">
        ${detRow(lbl1+" (cm)", it.widthCm>0 ? it.widthCm : "—", true)}
        ${detRow(lbl2+" (cm)", it.heightCm>0 ? it.heightCm : "—", true)}
        ${it.note ? detRow("Not", it.note) : ""}
      </table>
      ${sec ? `<div style="margin-top:7px;padding:7px 10px;background:${sec.bg};border:1.5px solid ${sec.border};border-radius:6px;display:flex;align-items:center;gap:10px">
        <span style="font-size:9px;font-weight:800;text-transform:uppercase;letter-spacing:1px;color:${sec.fg}">${sec.zone}</span>
        <span style="font-size:10.5px;font-weight:700;color:${sec.fg}">${sec.bolme} Bölme · ${sec.ray} Ray</span>
      </div>` : ""}
    </div>
    <div>
      ${accs.length ? `<div style="font-size:8px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:#94A3B8;margin-bottom:5px">AKSESUAR</div>
      <table style="width:100%;border-collapse:collapse">${accs.map(([k,v])=>detRow(k,v)).join("")}</table>` : ""}
      ${(renkLines.length || fabricImgSrc) ? `<div style="font-size:8px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:#94A3B8;margin-top:8px;margin-bottom:5px">RENK / FİNİŞ</div>
      <div style="display:flex;align-items:flex-start;gap:8px">
        <table style="width:100%;border-collapse:collapse;flex:1">${renkLines.map(([k,v])=>detRow(k,v)).join("")}</table>
        ${fabricImgSrc ? `<div style="flex-shrink:0;text-align:center">
          <img src="${fabricImgSrc}" style="width:56px;height:56px;object-fit:cover;border-radius:6px;border:1.5px solid #CBD5E1;display:block">
          <div style="font-size:7px;color:#94A3B8;margin-top:2px;max-width:56px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(it.fabric?.split(" (")[0]||"")}</div>
        </div>` : ""}
      </div>` : ""}
    </div>
  </div>
  ${ins.length ? `<div style="padding:8px 10px;background:#F8FAFC;border:1px solid #E2E8F0;border-radius:6px;margin-bottom:10px">
    <div style="font-size:8px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:#64748B;margin-bottom:5px">İNŞAİ AKSESUARLAR</div>
    <div style="display:flex;flex-wrap:wrap;gap:5px">${ins.map(l=>`<span style="background:#E2E8F0;padding:3px 8px;border-radius:4px;font-size:9.5px;font-weight:600;color:#334155">${esc(l)}</span>`).join("")}</div>
  </div>` : ""}
  <!-- price row -->
  <div style="display:flex;justify-content:flex-end;align-items:center;gap:10px;flex-wrap:wrap">
    ${isAdmin ? `
    <div style="padding:6px 12px;background:#FEF9C3;border:1px solid #FDE047;border-radius:6px;text-align:right">
      <div style="font-size:7px;text-transform:uppercase;letter-spacing:1px;color:#854D0E;font-weight:700">ALIŞ BİRİM</div>
      <div style="font-size:11px;font-weight:800;color:#92400E">${fmtEur(alisUnitPrice)}</div>
    </div>
    <div style="padding:6px 12px;background:#FEF9C3;border:1px solid #FDE047;border-radius:6px;text-align:right">
      <div style="font-size:7px;text-transform:uppercase;letter-spacing:1px;color:#854D0E;font-weight:700">ALIŞ TOPLAM</div>
      <div style="font-size:11px;font-weight:800;color:#92400E">${fmtEur(alisToplam)}</div>
    </div>` : ""}
    <div style="padding:6px 12px;background:#F0FDF4;border:1px solid #86EFAC;border-radius:6px;text-align:right">
      <div style="font-size:7px;text-transform:uppercase;letter-spacing:1px;color:#166534;font-weight:700">SATIŞ BİRİM</div>
      <div style="font-size:11px;font-weight:800;color:#166534">${fmtEur(satisBirim)}</div>
    </div>
    <div style="padding:7px 14px;background:#1E293B;border-radius:6px;text-align:right">
      <div style="font-size:7px;text-transform:uppercase;letter-spacing:1px;color:#94A3B8;font-weight:700">KALEM TOPLAMI</div>
      <div style="font-size:13px;font-weight:900;color:#FFFFFF">${fmtEur(satisToplam)}</div>
    </div>
  </div>
</div>`;
  }).join("\n");

  // grand totals
  const gtSatis = items.reduce((s,it)=>s+(Number(it.unitPrice||0)*(it.qty||1)),0);
  const gtAlis  = gtSatis/1.20;
  const gtKar   = gtSatis-gtAlis;
  const dateStr = new Date(order.createdAt||Date.now()).toLocaleDateString("tr-TR");

  return `<!DOCTYPE html><html lang="tr"><head><meta charset="utf-8"><meta name="color-scheme" content="light">
<title>Tenteks Sipariş Föyü — ${esc(ono(order))}</title>
<style>
  @page{size:A4 portrait;margin:13mm 11mm}
  *{box-sizing:border-box}
  html,body{background:#fff;color-scheme:light;margin:0;font-family:'Segoe UI',Arial,sans-serif;color:#1E293B;font-size:11px}
</style></head><body>
<div style="display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:10px;border-bottom:3px solid #E5322A;margin-bottom:14px">
  <div>
    <div style="font-size:8px;letter-spacing:3px;text-transform:uppercase;color:#94A3B8;font-weight:700">ZEN Mekanizmalı Perde Sistemleri</div>
    <div style="font-size:20px;font-weight:900;letter-spacing:2px;color:#1E293B;margin-top:3px">SİPARİŞ FÖYÜ</div>
    <div style="font-size:10px;color:#64748B;margin-top:2px;font-weight:600">Tenteks Gölgelendirme Sistemleri</div>
  </div>
  <div style="text-align:right">
    <div style="font-size:8px;color:#94A3B8;font-weight:700;text-transform:uppercase">Belge No</div>
    <div style="font-size:14px;font-weight:800;color:#E5322A">${esc(ono(order)||"-")}</div>
    <div style="font-size:10px;color:#475569;margin-top:3px">Müşteri: <b>${esc(order.customerName||"-")}</b></div>
    <div style="font-size:10px;color:#475569">Tarih: <b>${dateStr}</b></div>
    ${order.projectName?`<div style="font-size:10px;color:#475569">Proje: <b>${esc(order.projectName)}</b></div>`:""}
  </div>
</div>
${cards}
<div style="padding:12px 14px;background:#F8FAFC;border:1.5px solid #CBD5E1;border-radius:8px;margin-top:4px">
  <div style="font-size:8px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#64748B;margin-bottom:8px">TENTEKS ÜRÜNLER ÖZET</div>
  <div style="display:flex;justify-content:flex-end;gap:10px;flex-wrap:wrap;align-items:center">
    ${isAdmin?`
    <div style="padding:8px 14px;background:#FEF9C3;border:1px solid #FDE047;border-radius:6px;text-align:right">
      <div style="font-size:7px;text-transform:uppercase;letter-spacing:1px;color:#854D0E;font-weight:700">TOPLAM ALIŞ</div>
      <div style="font-size:14px;font-weight:900;color:#92400E">${fmtEur(gtAlis)}</div>
    </div>
    <div style="padding:8px 14px;background:#D1FAE5;border:1px solid #34D399;border-radius:6px;text-align:right">
      <div style="font-size:7px;text-transform:uppercase;letter-spacing:1px;color:#065F46;font-weight:700">BRÜT KAR</div>
      <div style="font-size:14px;font-weight:900;color:#065F46">${fmtEur(gtKar)}</div>
    </div>`:""}
    <div style="padding:8px 16px;background:#1E293B;border-radius:6px;text-align:right">
      <div style="font-size:7px;text-transform:uppercase;letter-spacing:1px;color:#94A3B8;font-weight:700">TOPLAM SATIŞ (KDV Hariç)</div>
      <div style="font-size:16px;font-weight:900;color:#FFFFFF">${fmtEur(gtSatis)}</div>
    </div>
  </div>
</div>
<div style="margin-top:8px;font-size:8px;color:#94A3B8;text-align:center">ZEN Mekanizmalı Perde Sistemleri · Tedarikçi: Tenteks · Bu belge yalnızca iç kullanım içindir${isAdmin?" · ADMİN — GİZLİ FİYAT BİLGİSİ İÇERİR":""}</div>
</body></html>`;
}

